



























import { computed, defineComponent, ref, useContext, useFetch, useRoute, watch } from '@nuxtjs/composition-api';
import { uniqBy, debounce } from 'lodash-es';
import StaffProductCard from '~/components/pages/Staff/StaffProductCard.vue';
import StaffReviewCard from '~/components/pages/Staff/StaffReviewCard.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import GlobalHeaderMenu from '~/components/partials/Global/GlobalHeaderMenu.vue';
import LayoutDefault from '~/components/partials/Layout/LayoutDefault.vue';
import StaffHeader from '~/components/partials/Staff/StaffHeader.vue';
import { LayoutProps } from '~/composables/internals/useLayoutPropsProvider';
import { useCancellable } from '~/composables/useCancellable';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useStore } from '~/composables/useStore';
import { REVIEWS_PER_PAGE, STAFF_REVIEW_SEARCH_SORT_OPTIONS } from '~/constants';
import { url } from '~/utils';
import { PageError } from '~/utils/error';
import { ProductShowECResponseSchema, ProductListItem } from '~/types/modules/product';
import { StaffReview } from '~/types/modules/staff';
import type { BreadcrumbsItem } from '~/types/ui';
import { BooleanLike, ProductCategoryId, ProductTypeId } from '~/enums';

export default defineComponent({
  components: {
    LayoutDefault,
    StaffHeader,
    AppSeeMoreButton,
    StaffReviewCard,
    StaffProductCard,
    GlobalHeaderMenu,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const { i18n } = useContext();
    const { productCode } = route.value.params;
    const ecDetailResponse = ref<ProductShowECResponseSchema | null>(null);
    const reviews = ref<StaffReview[]>([]);
    const reviewCount = ref<number>(0);
    const pagination = ref(0);
    const sort = ref(STAFF_REVIEW_SEARCH_SORT_OPTIONS[0].value);
    const product = ref<ProductListItem[]>([]);
    const productName = computed(() => ecDetailResponse.value?.langs?.find(({ id }) => id === 'ja')?.name || '');
    const SEOTitle = computed(() => i18n.t('Item-20.pageTitle', { productName: productName.value }).toString());
    const cancellable = useCancellable(['fetchStaffReview']);

    const { fetch, fetchState } = useFetch(async () => {
      try {
        ecDetailResponse.value = await store.dispatch('product/detail/fetchECProductDetail', {
          path: { code: productCode },
        });
        // 商品種別がプリムールだったら404
        if (ecDetailResponse.value?.type_id === ProductTypeId.PRIMEUR) {
          throw new PageError(404, {});
        }
      } catch {
        throw new PageError(404, {});
      }

      try {
        const { items, total } = await store.dispatch('modules/staff/fetchStaffReviewList', {
          productCode,
          sort: sort.value,
          offset: pagination.value * REVIEWS_PER_PAGE,
          cancelToken: cancellable.token('fetchStaffReview'),
        });

        if (items.length === 0) {
          throw new PageError(404, {});
        }

        reviews.value = uniqBy([...reviews.value, ...items], 'id');
        reviewCount.value = total;
      } catch {
        throw new PageError(404, {});
      }
    });

    useLayoutProps(() => {
      const breadcrumbs: BreadcrumbsItem[] = [];
      const layoutProps: LayoutProps = {
        layout: {
          header: 'always',
          headerMenu: 'never',
        },
        head: {
          title: SEOTitle.value,
          description: i18n.t('Item-20.description').toString(),
        },
        breadcrumbs,
        fetchState,
      };

      if (!ecDetailResponse.value) {
        return layoutProps;
      }

      const { categories, master_settings } = ecDetailResponse.value;
      const path = url('ITEM_LIST');
      const categoryIds = categories
        .map(({ id }) => id.toString())
        .filter((id) => id !== ProductCategoryId.WINE.toString());
      categories.forEach((category, index) => {
        breadcrumbs.push({
          title: category.name,
          to: { path, query: { _ctg: categoryIds.slice(0, index + 1), _qty: BooleanLike.TRUE } },
        });
      });
      if (master_settings?.color && master_settings.color.name && master_settings.color.id) {
        breadcrumbs.push({
          title: master_settings.color.name,
          to: { path, query: { _color: master_settings.color.id.toString(), _qty: BooleanLike.TRUE } },
        });
      }
      if (master_settings?.producing_areas) {
        master_settings.producing_areas.forEach((area) => {
          if (area.id && area.name) {
            breadcrumbs.push({
              title: area.name,
              to: { path, query: { _area: area.id.toString(), _qty: BooleanLike.TRUE } },
            });
          }
        });
      }
      if (master_settings?.producer && master_settings?.producer?.name_kana && master_settings?.producer?.id) {
        breadcrumbs.push({
          title: master_settings.producer.name_kana,
          to: { path, query: { _producers: master_settings.producer.id.toString(), _qty: BooleanLike.TRUE } },
        });
      }
      breadcrumbs.push({ title: productName.value, to: url('ITEM_DETAIL', { code: productCode }) });
      breadcrumbs.push({ title: SEOTitle.value, to: null });

      return layoutProps;
    });

    const isLoadMoreButtonVisible = computed(() => reviewCount.value > reviews.value.length);

    watch(
      pagination,
      debounce(async () => {
        cancellable.cancel(['fetchStaffReview']);
        await fetch();
      }, 100)
    );

    watch(
      sort,
      debounce(async () => {
        cancellable.cancel(['fetchStaffReview']);
        reviews.value = [];
        await fetch();
      }, 100)
    );

    return {
      reviews,
      reviewCount,
      isLoadMoreButtonVisible,
      sort,
      pagination,
      product,
      ecDetailResponse,
      productName,
      SEOTitle,
    };
  },
});
